// src/services/api_client.ts

import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  CreateAxiosDefaults,
} from "axios";
import { store } from "../store/store";
import { logout } from "../store/auth_slice";

// API configuration constants
const API_BASE_URL = import.meta.env.VITE_API_URL;
const AUTH_TOKEN_KEY = "auth_token";

/**
 * ApiClient class handles all HTTP requests and provides consistent error handling
 * and authentication management across the application.
 */
class ApiClient {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private refreshSubscribers: ((token: string) => void)[] = [];

  constructor() {

    // Initial axios configuration
    const axiosConfig: CreateAxiosDefaults = {
      baseURL: `${API_BASE_URL}/api`,
      timeout: 30000, // 30 second timeout
      withCredentials: true, // Required for CORS with credentials
    };

    this.instance = axios.create(axiosConfig);
    this.setupInterceptors();
  }

  /**
   * Sets up request and response interceptors for authentication and error handling
   */
  private setupInterceptors(): void {
    this.instance.interceptors.request.use(
      this.handleRequest,
      this.handleRequestError
    );

    this.instance.interceptors.response.use(
      this.handleResponse,
      this.handleResponseError
    );
  }

  /**
   * Handles outgoing requests by adding authentication token if available
   */
  private handleRequest = (
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Handle Content-Type headers
    config.headers = config.headers || {};
    
    // If sending FormData, let the browser set the Content-Type header
    if (config.data instanceof FormData) {
      // Remove Content-Type header so browser can set it with boundary
      delete config.headers['Content-Type'];
    } 
    // For regular data, set Content-Type to application/json
    else if (config.data && config.method !== 'get') {
      config.headers['Content-Type'] = 'application/json';
    }
    
    return config;
  };

  /**
   * Handles request errors
   */
  private handleRequestError = (error: AxiosError): Promise<AxiosError> => {
    console.error("Request error:", error);
    return Promise.reject(error);
  };

  /**
   * Processes successful responses
   */
  private handleResponse = (response: AxiosResponse): AxiosResponse => {
    return response;
  };

  /**
   * Handles response errors and manages authentication failures
   */
  private handleResponseError = async (
    error: AxiosError
  ): Promise<AxiosResponse<unknown>> => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Only handle 401 errors that haven't been retried
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Handle concurrent requests during token refresh
    if (this.isRefreshing) {
      try {
        const token = await new Promise<string>((resolve) => {
          this.refreshSubscribers.push(resolve);
        });
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return this.instance(originalRequest);
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // Mark request for retry
    originalRequest._retry = true;
    this.isRefreshing = true;

    try {
      const refreshToken = localStorage.getItem("refresh_token");
      
      if (!refreshToken) {
        // No refresh token available, clean up and logout
        localStorage.removeItem(AUTH_TOKEN_KEY);
        store.dispatch(logout());
        return Promise.reject(error);
      }
      
      // Attempt to refresh the token
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refresh`,
        { refreshToken }
      );
      
      if (response.data && response.data.accessToken) {
        const newToken = response.data.accessToken;
        
        // Update stored token
        localStorage.setItem(AUTH_TOKEN_KEY, newToken);
        
        // Update request Authorization header
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Notify all the waiting requests
        this.refreshSubscribers.forEach(callback => callback(newToken));
        this.refreshSubscribers = [];
        
        // Retry the original request with the new token
        return this.instance(originalRequest);
      } else {
        // Invalid response from refresh endpoint
        throw new Error("Failed to refresh authentication token");
      }
    } catch (refreshError) {
      // If token refresh fails, clean up and logout
      console.error("Token refresh failed:", refreshError);
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem("refresh_token");
      store.dispatch(logout());
      return Promise.reject(error);
    } finally {
      this.isRefreshing = false;
    }
  };

  /**
   * Common error handler for all requests
   */
  private handleError(error: unknown) {
    if (axios.isAxiosError(error)) {
      // Log detailed error information for debugging
      console.error("API Error:", {
        status: error.response?.status,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
        }
      });

      // Don't automatically logout on 401 here as we handle token refresh in interceptor
      // The response interceptor will handle auth failures after refresh attempts
    }
  }

  /**
   * Performs GET request
   */
  public async get<T = unknown>(
    url: string,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.instance.get(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Performs POST request
   */
  public async post<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.instance.post(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Performs PUT request
   */
  public async put<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.instance.put(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Performs DELETE request
   */
  public async delete<T = unknown>(
    url: string,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.instance.delete(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  /**
   * Performs PATCH request
   */
  public async patch<T = unknown>(
    url: string,
    data?: unknown,
    config?: InternalAxiosRequestConfig
  ): Promise<T> {
    try {
      const response = await this.instance.patch(url, data, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error;
    }
  }

  // --- Stripe Methods ---

  /**
   * Creates a Stripe Connect account onboarding link.
   * Uses backend endpoint POST /api/stripe/connect/onboarding-link
   * and returns { url: string }.
   */
  public async createStripeAccountLink(): Promise<{ url: string }> {
    try {
      const response = await this.instance.post<{ url: string }>(
        '/stripe/connect/onboarding-link'
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw new Error("Failed to create Stripe account link.");
    }
  }

  /**
   * Creates a Stripe Connect dashboard link (previously portal session).
   * Uses backend endpoint GET /api/stripe/connect/dashboard-link
   * and returns { url: string }.
   */
  public async createStripePortalSession(): Promise<{ url: string }> {
    try {
      const response = await this.instance.get<{ url: string }>(
        '/stripe/connect/dashboard-link'
      );
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw new Error("Failed to create Stripe dashboard link.");
    }
  }
}

// Create and export a single instance
const apiClient = new ApiClient();
export default apiClient;